import React, { useState, useEffect, lazy } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    MenuItem,
    Menu,
    IconButton
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { LocalizationProvider, DatePicker, MonthPicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from 'date-fns/locale/fr';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StravaWidget from './StravaWidget';
import StravaActivities from './StravaActivities';

// Import dynamique de Leaflet pour éviter les problèmes SSR
const MapComponent = lazy(() => import('./MapComponent'));

const Home = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentEventIndex, setCurrentEventIndex] = useState(0);
    const [hiddenSports, setHiddenSports] = useState([]);
    const location = useLocation();
    const [searchName, setSearchName] = useState('');
    const [searchDate, setSearchDate] = useState(null);
    const [searchCity, setSearchCity] = useState('');
    const [searchMonth, setSearchMonth] = useState(null);
    const [legendMenuAnchor, setLegendMenuAnchor] = useState(null);

    // Faire défiler vers le haut de la page quand la route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // Images du Pérou et de sport pour le carrousel
    const backgroundImages = [
        'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=1920&h=1080&fit=crop', // Football
        'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1920&h=1080&fit=crop', // Basketball
        'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=1920&h=1080&fit=crop', // Volleyball
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop', // Machu Picchu
        'https://images.unsplash.com/photo-1589308078059-be1415eab4c3?w=1920&h=1080&fit=crop', // Lima
        'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1920&h=1080&fit=crop', // Tennis
    ];

    // Données des événements avec coordonnées géographiques
    const eventLocations = [
        {
            id: 1,
            title: 'Football Match - Liga 1',
            description: 'Alianza Lima vs Universitario de Deportes',
            date: 'December 15, 2024',
            location: 'Estadio Nacional, Lima',
            city: 'Lima',
            sport: 'Football',
            coordinates: [-12.0464, -77.0428], // Lima
            image: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&h=250&fit=crop',
            icon: <SportsSoccerIcon />,
        },
        {
            id: 2,
            title: 'Basketball Championship',
            description: 'National championship final',
            date: 'December 20, 2024',
            location: 'Coliseo Eduardo Dibós, Lima',
            city: 'Lima',
            sport: 'Basketball',
            coordinates: [-12.0464, -77.0428], // Lima
            image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=250&fit=crop',
            icon: <SportsBasketballIcon />,
        },
        {
            id: 3,
            title: 'Volleyball Tournament',
            description: "International women's competition",
            date: 'December 25, 2024',
            location: 'Coliseo Mariscal Cáceres, Lima',
            city: 'Lima',
            sport: 'Volleyball',
            coordinates: [-12.0464, -77.0428], // Lima
            image: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=250&fit=crop',
            icon: <SportsVolleyballIcon />,
        },
        {
            id: 4,
            title: 'Cusco Tennis Open',
            description: 'Clay court tennis tournament',
            date: 'January 10, 2025',
            location: 'Cusco Tennis Club',
            city: 'Cusco',
            sport: 'Tennis',
            coordinates: [-13.5167, -71.9789], // Cusco
            image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&h=250&fit=crop',
            icon: <SportsTennisIcon />,
        },
        {
            id: 5,
            title: 'Sporting Cristal vs Melgar',
            description: 'Clash between two giants',
            date: 'January 18, 2025',
            location: 'Estadio Alberto Gallardo, Lima',
            city: 'Lima',
            sport: 'Football',
            coordinates: [-12.0464, -77.0428], // Lima
            image: 'https://images.unsplash.com/photo-1517649763962-0c623066013b?w=400&h=250&fit=crop',
            icon: <SportsSoccerIcon />,
        },
        {
            id: 6,
            title: 'Karate Championship',
            description: 'National karate competition',
            date: 'January 22, 2025',
            location: 'Coliseo Arequipa',
            city: 'Arequipa',
            sport: 'Martial Arts',
            coordinates: [-16.4090, -71.5375], // Arequipa
            image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=250&fit=crop',
            icon: <SportsMartialArtsIcon />,
        },
        {
            id: 7,
            title: 'University Basketball',
            description: 'Match between universities',
            date: 'January 28, 2025',
            location: 'Coliseo de la UNMSM, Lima',
            city: 'Lima',
            sport: 'Basketball',
            coordinates: [-12.0464, -77.0428], // Lima
            image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop',
            icon: <SportsBasketballIcon />,
        },
        {
            id: 8,
            title: 'Beach Volleyball - Trujillo',
            description: 'Beach volleyball tournament',
            date: 'February 5, 2025',
            location: 'Huanchaco Beach, Trujillo',
            city: 'Trujillo',
            sport: 'Volleyball',
            coordinates: [-8.1090, -79.0215], // Trujillo
            image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=250&fit=crop',
            icon: <SportsVolleyballIcon />,
        },
    ];

    // Changement automatique d'image toutes les 5 secondes
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [backgroundImages.length]);

    // Changement automatique d'événement toutes les 8 secondes
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentEventIndex((prevIndex) =>
                prevIndex === eventLocations.length - 1 ? 0 : prevIndex + 1
            );
        }, 10000);

        return () => clearInterval(interval);
    }, [eventLocations.length]);

    // Fonction pour gérer le clic sur un sport dans la légende
    const toggleSport = (sport) => {
        setHiddenSports(prev => {
            if (prev.includes(sport)) {
                return prev.filter(s => s !== sport);
            } else {
                return [...prev, sport];
            }
        });
    };

    const stats = [
        { label: 'Events', value: '150+', description: 'This year' },
        { label: 'Sports', value: '12', description: 'Different sports' },
        { label: 'Cities', value: '8', description: 'Across Peru' },
        { label: 'Spectators', value: '2M+', description: 'Per year' },
    ];

    // Liste des sports et villes uniques pour les filtres
    const uniqueSports = Array.from(new Set(eventLocations.map(e => e.sport)));
    const uniqueCities = Array.from(new Set(eventLocations.map(e => e.city)));

    // Filtrage des événements selon les filtres
    const filteredEvents = eventLocations.filter(event => {
        const matchName = searchName ? event.title.toLowerCase().includes(searchName.toLowerCase()) : true;
        const matchDate = searchDate ? (() => {
            const eventDate = new Date(event.date);
            // On compare les dates au format ISO (yyyy-mm-dd)
            return eventDate.toISOString().slice(0, 10) === searchDate.toISOString().slice(0, 10);
        })() : true;
        const matchMonth = searchMonth ? (() => {
            const eventDate = new Date(event.date);
            return (
                eventDate.getMonth() === searchMonth.getMonth() &&
                eventDate.getFullYear() === searchMonth.getFullYear()
            );
        })() : true;
        const matchCity = searchCity ? event.city === searchCity : true;
        return matchName && matchDate && matchMonth && matchCity;
    });

    // Ajout d'un effet pour réinitialiser l'autre filtre quand l'un est sélectionné
    useEffect(() => {
        if (searchDate) setSearchMonth(null);
    }, [searchDate]);
    useEffect(() => {
        if (searchMonth) setSearchDate(null);
    }, [searchMonth]);

    // Liste des sports uniques pour la légende (avec icône et couleur)
    const legendSports = [
        { sport: 'Football', color: '#FF6B6B', icon: '⚽' },
        { sport: 'Basketball', color: '#4ECDC4', icon: '🏀' },
        { sport: 'Volleyball', color: '#45B7D1', icon: '🏐' },
        { sport: 'Tennis', color: '#96CEB4', icon: '🎾' },
        { sport: 'Martial Arts', color: '#FFEAA7', icon: '🥋' },
        // Dynamic addition for other sports if needed
        ...uniqueSports.filter(s => !['Football', 'Basketball', 'Volleyball', 'Tennis', 'Martial Arts'].includes(s)).map(s => ({ sport: s, color: '#bdbdbd', icon: '❓' }))
    ];
    // Correction : on affiche toujours les 3 premiers, le reste dans le menu
    const mainLegend = legendSports.slice(0, 3);
    const extraLegend = legendSports.slice(3);

    return (
        <Box sx={{ pt: 0 }}>
            {/* Hero Section avec carrousel d'images */}
            <Box
                sx={{
                    position: 'relative',
                    color: 'white',
                    pt: 8,
                    pb: 10,
                    overflow: 'hidden',
                    minHeight: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {/* Carrousel d'images en arrière-plan */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                    }}
                >
                    {backgroundImages.map((image, index) => (
                        <Box
                            key={index}
                            component="img"
                            src={image}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: index === currentImageIndex ? 1 : 0,
                                transition: 'opacity 1.5s ease-in-out',
                            }}
                        />
                    ))}

                    {/* Overlay gradient pour améliorer la lisibilité */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6) 100%)',
                            zIndex: 1,
                        }}
                    />

                    {/* Lueur rouge au-dessus des images */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '30%',
                            background: 'linear-gradient(180deg, rgba(255, 107, 107, 0.3) 0%, rgba(255, 107, 107, 0.1) 50%, transparent 100%)',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    />
                </Box>

                {/* Indicateurs de carrousel */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 1,
                        zIndex: 3,
                    }}
                >
                    {backgroundImages.map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: index === currentImageIndex ? '#FF6B6B' : 'rgba(255,255,255,0.5)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: index === currentImageIndex ? '#FF6B6B' : 'rgba(255,255,255,0.8)',
                                },
                            }}
                            onClick={() => setCurrentImageIndex(index)}
                        />
                    ))}
                </Box>

                {/* Contenu principal */}
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'left' }}>
                                {/* Badge */}
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.15)',
                                        borderRadius: 3,
                                        px: 3,
                                        py: 1,
                                        mb: 3,
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#FF6B6B',
                                            mr: 2,
                                            animation: 'pulse 2s infinite',
                                            boxShadow: '0 0 10px rgba(255, 107, 107, 0.5)',
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'white' }}>
                                        Live events
                                    </Typography>
                                </Box>

                                {/* Titre principal */}
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                                        lineHeight: 1.1,
                                        mb: 3,
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
                                        color: 'white',
                                        textAlign: 'left',
                                    }}
                                >
                                    Discover
                                    <Box component="span" sx={{ display: 'block', color: '#FF6B6B', textShadow: '2px 2px 4px rgba(0,0,0,0.6)' }}>
                                        Sports Events
                                    </Box>
                                    <Box component="span" sx={{ display: 'block', color: 'white' }}>
                                        in Peru
                                    </Box>
                                </Typography>

                                {/* Sous-titre */}
                                <Typography
                                    variant="h5"
                                    paragraph
                                    sx={{
                                        mb: 4,
                                        opacity: 0.9,
                                        fontWeight: 300,
                                        lineHeight: 1.4,
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
                                        color: 'white',
                                        textAlign: 'left',
                                    }}
                                >
                                    Experience the passion of sports in one of South America's most vibrant destinations
                                </Typography>

                                {/* Statistiques rapides */}
                                <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF6B6B', textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}>
                                            150+
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8, textShadow: '1px 1px 2px rgba(0,0,0,0.6)', color: 'white' }}>
                                            Events
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF6B6B', textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}>
                                            12
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8, textShadow: '1px 1px 2px rgba(0,0,0,0.6)', color: 'white' }}>
                                            Sports
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF6B6B', textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}>
                                            8
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8, textShadow: '1px 1px 2px rgba(0,0,0,0.6)', color: 'white' }}>
                                            Cities
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Boutons d'action */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        component={Link}
                                        to="/events"
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            background: 'linear-gradient(45deg, #FF6B6B 30%, #E53E3E 90%)',
                                            color: 'white',
                                            px: 4,
                                            py: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #E53E3E 30%, #FF6B6B 90%)',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 35px rgba(255, 107, 107, 0.6)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        See all events
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            color: 'white',
                                            px: 4,
                                            py: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            backdropFilter: 'blur(10px)',
                                            '&:hover': {
                                                borderColor: 'white',
                                                backgroundColor: 'rgba(255,255,255,0.1)',
                                                transform: 'translateY(-2px)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        Learn more
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative',
                                    zIndex: 2,
                                }}
                            >
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Stats Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={4} justifyContent="center">
                    {stats.map((stat, index) => (
                        <Grid item xs={12} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 220,
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                    borderRadius: '20px',
                                    p: 4,
                                    textAlign: 'center',
                                    boxShadow: '0 20px 60px rgba(255, 107, 107, 0.4)',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    transform: 'perspective(1000px) rotateX(0deg)',
                                    '&:hover': {
                                        transform: 'perspective(1000px) rotateX(10deg) translateY(-15px) scale(1.05)',
                                        boxShadow: '0 30px 80px rgba(255, 107, 107, 0.6)',
                                        '& .stat-number': {
                                            transform: 'scale(1.2) rotateY(10deg)',
                                            textShadow: '0 0 30px rgba(255,255,255,0.8)',
                                        },
                                        '& .stat-glow': {
                                            opacity: 1,
                                            transform: 'scale(1.5)',
                                        },
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '-50%',
                                        left: '-50%',
                                        width: '200%',
                                        height: '200%',
                                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                                        transform: 'scale(0)',
                                        transition: 'transform 0.5s ease',
                                    },
                                    '&:hover::before': {
                                        transform: 'scale(1)',
                                    },
                                }}
                            >
                                {/* Effet de brillance */}
                                <Box
                                    className="stat-glow"
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        width: '100px',
                                        height: '100px',
                                        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
                                        borderRadius: '50%',
                                        transform: 'translate(-50%, -50%) scale(0)',
                                        opacity: 0,
                                        transition: 'all 0.5s ease',
                                        filter: 'blur(10px)',
                                    }}
                                />

                                {/* Contenu principal */}
                                <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Typography
                                        className="stat-number"
                                        variant="h1"
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: { xs: '3rem', md: '4rem' },
                                            color: 'white',
                                            mb: 2,
                                            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                            transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                            letterSpacing: '-2px',
                                        }}
                                    >
                                        {stat.value}
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 800,
                                            color: 'white',
                                            mb: 1,
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                            fontSize: { xs: '1rem', md: '1.2rem' },
                                        }}
                                    >
                                        {stat.label}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            textShadow: '0 1px 5px rgba(0,0,0,0.3)',
                                        }}
                                    >
                                        {stat.description}
                                    </Typography>
                                </Box>

                                {/* Particules décoratives */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        overflow: 'hidden',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {[...Array(5)].map((_, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                position: 'absolute',
                                                width: '4px',
                                                height: '4px',
                                                background: 'rgba(255,255,255,0.6)',
                                                borderRadius: '50%',
                                                top: `${Math.random() * 100}%`,
                                                left: `${Math.random() * 100}%`,
                                                animation: `float ${2 + Math.random() * 2}s ease-in-out infinite`,
                                                animationDelay: `${Math.random() * 2}s`,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Featured Events */}
            <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
                        Featured Events
                    </Typography>
                    <Typography variant="h6" textAlign="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
                        Discover the most popular sports events of the moment
                    </Typography>

                    {/* Carrousel d'événements */}
                    <Box sx={{ position: 'relative', mb: 4, display: 'flex', justifyContent: 'center' }}>
                        {/* Bouton précédent */}
                        <Box
                            onClick={() => setCurrentEventIndex(prev => prev === 0 ? eventLocations.length - 1 : prev - 1)}
                            sx={{
                                position: 'absolute',
                                left: -20,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 10,
                                boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: '0 6px 25px rgba(255, 107, 107, 0.5)',
                                },
                                '@media (max-width: 768px)': {
                                    left: 10,
                                },
                            }}
                        >
                            <Typography sx={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                                ‹
                            </Typography>
                        </Box>

                        {/* Bouton suivant */}
                        <Box
                            onClick={() => setCurrentEventIndex(prev => prev === eventLocations.length - 1 ? 0 : prev + 1)}
                            sx={{
                                position: 'absolute',
                                right: -20,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 10,
                                boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: '0 6px 25px rgba(255, 107, 107, 0.5)',
                                },
                                '@media (max-width: 768px)': {
                                    right: 10,
                                },
                            }}
                        >
                            <Typography sx={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                                ›
                            </Typography>
                        </Box>

                        {/* Conteneur du carrousel */}
                        <Box sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            minHeight: 320,
                            width: '100%',
                            maxWidth: 900
                        }}>
                            {eventLocations.map((event, index) => (
                                <Box
                                    key={event.id}
                                    sx={{
                                        position: 'absolute',
                                        width: '100%',
                                        maxWidth: 900,
                                        opacity: index === currentEventIndex ? 1 : 0,
                                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                        zIndex: index === currentEventIndex ? 2 : 1,
                                        left: '50%',
                                        transform: index === currentEventIndex
                                            ? 'translateX(-50%)'
                                            : `translateX(calc(-50% + ${(index - currentEventIndex) * 100}%))`,
                                    }}
                                >
                                    <Card
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            width: '100%',
                                            border: '1px solid rgba(255, 107, 107, 0.1)',
                                        }}
                                    >
                                        {/* Image */}
                                        <Box sx={{
                                            position: 'relative',
                                            width: { xs: '100%', md: '40%' },
                                            minHeight: { xs: 200, md: 280 }
                                        }}>
                                            <CardMedia
                                                component="img"
                                                height="100%"
                                                image={event.image}
                                                alt={event.title}
                                                sx={{
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                            {/* Overlay gradient */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(229, 62, 62, 0.1) 100%)',
                                                    opacity: 0,
                                                    transition: 'opacity 0.3s ease',
                                                    '&:hover': {
                                                        opacity: 1,
                                                    },
                                                }}
                                            />
                                            {/* Badge sport */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 20,
                                                    left: 20,
                                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                                    color: 'white',
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
                                                }}
                                            >
                                                <Box sx={{
                                                    fontSize: '1.2rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    lineHeight: 1
                                                }}>
                                                    {event.icon}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    fontSize="0.8rem"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        lineHeight: 1
                                                    }}
                                                >
                                                    {event.sport}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Contenu */}
                                        <Box sx={{
                                            flex: 1,
                                            p: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Box>
                                                <Typography
                                                    variant="h5"
                                                    component="h3"
                                                    gutterBottom
                                                    sx={{
                                                        fontWeight: 800,
                                                        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                                                        backgroundClip: 'text',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        mb: 1,
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {event.title}
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    paragraph
                                                    sx={{
                                                        color: '#7f8c8d',
                                                        fontSize: '1rem',
                                                        lineHeight: 1.5,
                                                        mb: 2,
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {event.description}
                                                </Typography>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        background: 'rgba(255, 107, 107, 0.05)',
                                                        border: '1px solid rgba(255, 107, 107, 0.1)',
                                                    }}>
                                                        <CalendarTodayIcon sx={{ fontSize: 18, mr: 1.5, color: '#FF6B6B' }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                                            {event.date}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        background: 'rgba(255, 107, 107, 0.05)',
                                                        border: '1px solid rgba(255, 107, 107, 0.1)',
                                                    }}>
                                                        <LocationOnIcon sx={{ fontSize: 18, mr: 1.5, color: '#FF6B6B' }} />
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#FF3B3B',
                                                                textDecoration: 'underline',
                                                                fontWeight: 600,
                                                                fontSize: '1rem',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {event.location}
                                                        </a>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: 3 }}>
                                                <Button
                                                    component={Link}
                                                    to={`/event/${event.id}`}
                                                    variant="contained"
                                                    fullWidth
                                                    size="medium"
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                                        color: 'white',
                                                        py: 1.5,
                                                        fontSize: '1rem',
                                                        fontWeight: 700,
                                                        borderRadius: 3,
                                                        textTransform: 'none',
                                                        boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #E53E3E 0%, #FF6B6B 100%)',
                                                            boxShadow: '0 12px 35px rgba(255, 107, 107, 0.4)',
                                                        },
                                                        transition: 'all 0.3s ease',
                                                    }}
                                                >
                                                    See details
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Indicateurs */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {eventLocations.map((_, index) => (
                            <Box
                                key={index}
                                onClick={() => setCurrentEventIndex(index)}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: index === currentEventIndex ? '#FF6B6B' : 'rgba(0,0,0,0.2)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: index === currentEventIndex ? '#FF6B6B' : 'rgba(255, 107, 107, 0.5)',
                                        transform: 'scale(1.2)',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Carte Interactive du Pérou */}
            <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" gutterBottom>
                        Sports Events Map
                    </Typography>
                    <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 6 }}>
                        Discover where sports events take place across Peru
                    </Typography>

                    {/* Filtres de recherche */}
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
                        <Paper elevation={3} sx={{
                            mb: 5,
                            px: { xs: 2, md: 4 },
                            py: 3,
                            borderRadius: 4,
                            background: 'rgba(255,255,255,0.95)',
                            boxShadow: '0 8px 32px rgba(255,107,107,0.10)',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2.5,
                            alignItems: 'center',
                            justifyContent: { xs: 'center', md: 'flex-start' },
                            position: 'relative',
                            zIndex: 2,
                            maxWidth: { xs: '100%', lg: 1060 },
                            width: '100%',
                        }}>
                            <TextField
                                label="Search by name"
                                variant="outlined"
                                size="small"
                                value={searchName}
                                onChange={e => setSearchName(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#FF6B6B' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    minWidth: 220,
                                    background: 'white',
                                    borderRadius: 3,
                                    boxShadow: '0 2px 8px rgba(255,107,107,0.07)',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        fontWeight: 500,
                                        transition: 'box-shadow 0.3s',
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                    '& label.Mui-focused': {
                                        color: '#FF6B6B',
                                    },
                                }}
                            />
                            {(!searchMonth) && (
                                <DatePicker
                                    label="Filter by date"
                                    value={searchDate}
                                    onChange={setSearchDate}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                            sx: {
                                                minWidth: 180,
                                                background: 'white',
                                                borderRadius: 3,
                                                boxShadow: '0 2px 8px rgba(255,107,107,0.07)',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    fontWeight: 500,
                                                    transition: 'box-shadow 0.3s',
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF6B6B',
                                                        boxShadow: '0 0 0 2px #FF6B6B33',
                                                    },
                                                },
                                                '& label.Mui-focused': {
                                                    color: '#FF6B6B',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#FF6B6B',
                                                    fontSize: 26,
                                                },
                                            },
                                        },
                                    }}
                                    format="dd MMMM yyyy"
                                />)}
                            {(!searchDate) && (
                                <DatePicker
                                    views={['year', 'month']}
                                    label="Filter by month/year"
                                    minDate={new Date(2024, 0, 1)}
                                    value={searchMonth}
                                    onChange={setSearchMonth}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                            sx: {
                                                minWidth: 180,
                                                background: 'white',
                                                borderRadius: 3,
                                                boxShadow: '0 2px 8px rgba(255,107,107,0.07)',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    fontWeight: 500,
                                                    transition: 'box-shadow 0.3s',
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF6B6B',
                                                        boxShadow: '0 0 0 2px #FF6B6B33',
                                                    },
                                                },
                                                '& label.Mui-focused': {
                                                    color: '#FF6B6B',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#FF6B6B',
                                                    fontSize: 26,
                                                },
                                            },
                                        },
                                    }}
                                    format="MMMM yyyy"
                                />)}
                            <TextField
                                select
                                label="Filter by city"
                                variant="outlined"
                                size="small"
                                value={searchCity}
                                onChange={e => setSearchCity(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationCityIcon sx={{ color: '#FF6B6B' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    minWidth: 180,
                                    background: 'white',
                                    borderRadius: 3,
                                    boxShadow: '0 2px 8px rgba(255,107,107,0.07)',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        fontWeight: 500,
                                        transition: 'box-shadow 0.3s',
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                    '& label.Mui-focused': {
                                        color: '#FF6B6B',
                                    },
                                }}
                            >
                                <MenuItem value="">All cities</MenuItem>
                                {uniqueCities.map(city => (
                                    <MenuItem key={city} value={city}>{city}</MenuItem>
                                ))}
                            </TextField>
                            {(searchName || searchDate || searchMonth || searchCity) && (
                                <Button
                                    onClick={() => { setSearchName(''); setSearchDate(null); setSearchMonth(null); setSearchCity(''); }}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        color: '#FF6B6B',
                                        borderColor: '#FF6B6B',
                                        fontWeight: 700,
                                        borderRadius: 3,
                                        background: 'white',
                                        boxShadow: '0 2px 8px rgba(255,107,107,0.07)',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #FF6B6B11 0%, #E53E3E11 100%)',
                                            borderColor: '#E53E3E',
                                            color: '#E53E3E',
                                        },
                                    }}
                                >
                                    Reset
                                </Button>
                            )}
                        </Paper>
                    </LocalizationProvider>

                    <Grid container spacing={4}>
                        {/* Carte */}
                        <Grid item xs={12} lg={8}>
                            <Paper sx={{ p: 2, borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                                <Box
                                    sx={{
                                        height: 670,
                                        width: 750,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        backgroundColor: '#f5f5f5',
                                    }}
                                >
                                    <React.Suspense fallback={
                                        <Box sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f5f5f5'
                                        }}>
                                            <Typography variant="h6" color="text.secondary">
                                                Chargement de la carte...
                                            </Typography>
                                        </Box>
                                    }>
                                        <MapComponent eventLocations={filteredEvents} hiddenSports={hiddenSports} />
                                    </React.Suspense>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Légende */}
                        <Grid item xs={12} lg={4}>
                            <Paper sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                height: 700,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#2c3e50' }}>
                                    Legend
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                                    Click on a sport to hide/show it
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                                    {mainLegend.map(({ sport, color, icon }) => {
                                        const isHidden = hiddenSports.includes(sport);
                                        return (
                                            <Box
                                                key={sport}
                                                onClick={() => toggleSport(sport)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: isHidden ? '#f5f5f5' : 'transparent',
                                                    border: isHidden ? '2px dashed #ccc' : '2px solid transparent',
                                                    opacity: isHidden ? 0.5 : 1,
                                                    '&:hover': {
                                                        backgroundColor: isHidden ? '#f0f0f0' : '#f8f9fa',
                                                        transform: 'translateX(4px)',
                                                    }
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50% 50% 50% 0',
                                                    transform: 'rotate(-45deg)',
                                                    background: isHidden
                                                        ? 'linear-gradient(135deg, #ccc 0%, #ccc 100%)'
                                                        : `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                                    border: '2px solid white',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '14px',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    transition: 'all 0.3s ease'
                                                }}>
                                                    <Box sx={{ transform: 'rotate(45deg)' }}>{icon}</Box>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: isHidden ? '#999' : '#2c3e50',
                                                            textDecoration: isHidden ? 'line-through' : 'none',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        {sport}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: isHidden ? '#ccc' : '#666',
                                                            fontStyle: 'italic'
                                                        }}
                                                    >
                                                        {isHidden ? 'Hidden' : 'Visible'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: isHidden ? '#ccc' : '#4CAF50',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '12px',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {isHidden ? '×' : '✓'}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    {extraLegend.length > 0 && (
                                        <>
                                            <IconButton
                                                onClick={e => setLegendMenuAnchor(e.currentTarget)}
                                                sx={{ alignSelf: 'flex-start', mt: 1, color: '#FF6B6B', background: '#fff', border: '1px solid #eee', boxShadow: '0 2px 8px rgba(255,107,107,0.07)' }}
                                            >
                                                <MoreHorizIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={legendMenuAnchor}
                                                open={Boolean(legendMenuAnchor)}
                                                onClose={() => setLegendMenuAnchor(null)}
                                                PaperProps={{ sx: { borderRadius: 3, minWidth: 220, p: 1 } }}
                                            >
                                                {extraLegend.map(({ sport, color, icon }) => {
                                                    const isHidden = hiddenSports.includes(sport);
                                                    return (
                                                        <MenuItem
                                                            key={sport}
                                                            onClick={() => { toggleSport(sport); setLegendMenuAnchor(null); }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                opacity: isHidden ? 0.5 : 1,
                                                                textDecoration: isHidden ? 'line-through' : 'none',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50% 50% 50% 0',
                                                                transform: 'rotate(-45deg)',
                                                                background: isHidden
                                                                    ? 'linear-gradient(135deg, #ccc 0%, #ccc 100%)'
                                                                    : `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                                                border: '2px solid white',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '13px',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                transition: 'all 0.3s ease',
                                                                mr: 1
                                                            }}>
                                                                <Box sx={{ transform: 'rotate(45deg)' }}>{icon}</Box>
                                                            </Box>
                                                            {sport}
                                                            <Box sx={{ ml: 'auto', color: isHidden ? '#ccc' : '#4CAF50', fontWeight: 'bold' }}>{isHidden ? '×' : '✓'}</Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Menu>
                                        </>
                                    )}
                                </Box>

                                {/* Bouton pour tout afficher */}
                                {hiddenSports.length > 0 && (
                                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                                        <Button
                                            onClick={() => setHiddenSports([])}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            sx={{
                                                borderColor: '#FF6B6B',
                                                color: '#FF6B6B',
                                                '&:hover': {
                                                    borderColor: '#E53E3E',
                                                    backgroundColor: 'rgba(255, 107, 107, 0.05)',
                                                }
                                            }}
                                        >
                                            Show all sports
                                        </Button>
                                    </Box>
                                )}

                                {/* Bouton CTA */}
                                <Box sx={{ mt: 'auto', pt: 2 }}>
                                    <Button
                                        component={Link}
                                        to="/events"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                            color: 'white',
                                            py: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #E53E3E 0%, #FF6B6B 100%)',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 35px rgba(255, 107, 107, 0.4)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        See all events
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

export default Home; 